/**
 * /reports — the performance dashboard.
 *
 * Search-param contract:
 *   - `period` : one of the eight preset slugs. Mutually exclusive with
 *                `from`/`to`; absent ⇒ `this-month`.
 *   - `from` / `to` : `YYYY-MM-DD` custom window, honoured only when BOTH
 *                are present and ordered.
 *   - `gran`   : 'week' | 'month'. Honoured only above 31 days, where the
 *                toggle is actually offered.
 *   - `edit`   : `1` ⇒ Customize mode (`true` / `'1'` / `'true'` are also
 *                accepted on read, and normalized to `1`).
 *
 * Param-name note: `period`, NOT `range`. TanStack Router's search-param
 * registry UNIONS same-named keys across every route, and `/approvals`
 * already owns `range` with a different union — a `range` here would leak
 * into its typing and break its `(prev) => …` navigate reducers. Same
 * class of collision the timesheet dodged by naming its param `scope`.
 *
 * Per-widget config is deliberately absent from the URL: it is per-user
 * persisted state, and putting it here would let a shared link rewrite
 * the recipient's saved dashboard.
 *
 * Guard order mirrors `timesheet.tsx` + `settings/index.tsx`:
 *
 *   1. no access token            → /login
 *   2. `me` still pending         → skeleton (never mount fetches before
 *                                   permissions resolve — a reports fetch
 *                                   fired at a user who turns out to lack
 *                                   the slug is a guaranteed 403)
 *   3. guest                      → navigate('/')
 *   4. missing `view-reports`     → <ReportsNoAccess> PANEL, not a bounce
 *   5. otherwise                  → <ReportsPage>
 *
 * Step 4 resolves rather than redirects on purpose, and the two refusals
 * differ for a reason. Guest-ness is a hard ceiling nobody can grant
 * away, so a guest is sent to a surface they DO have. A missing slug is
 * grantable by any administrator — bouncing would hide the fix, and it
 * would also break a `/docs/` deep link into this page by silently
 * landing the reader somewhere else. The settings tabs set the precedent.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { getAccessToken } from '@/auth/cookie-helpers'
import { useIsGuest } from '@/features/me/guest'
import { useMe } from '@/features/me/use-me'
import { useReportsPermission } from '@/features/me/use-reports-permission'
import { ReportsNoAccess } from '@/features/reports/components/ReportsNoAccess'
import { ReportsPage } from '@/features/reports/components/ReportsPage'
import { ReportsSkeleton } from '@/features/reports/components/ReportsSkeleton'
import type { GranularityOverride, ReportsSearchParams } from '@/features/reports/lib/window'
import { isIsoDate } from '@/features/reports/lib/window'
import { REPORT_PERIOD_PRESETS } from '@/features/reports/types'
import type { DateRangePreset } from '@/features/time/date-helpers'

const GRANULARITY_VALUES: readonly GranularityOverride[] = ['week', 'month']

export const Route = createFileRoute('/reports')({
  component: ReportsRoute,
  validateSearch: (search: Record<string, unknown>): ReportsSearchParams => {
    const out: ReportsSearchParams = {}

    const period = search.period
    if (
      typeof period === 'string' &&
      (REPORT_PERIOD_PRESETS as readonly string[]).includes(period)
    ) {
      out.period = period as DateRangePreset
    }

    // `from` and `to` are validated independently here and PAIRED in
    // `resolveDashboardWindow` — a half-supplied range is not an error,
    // it just doesn't take effect, so the page falls back to the preset
    // instead of rendering a validation failure the user can't act on.
    if (isIsoDate(search.from)) out.from = search.from
    if (isIsoDate(search.to)) out.to = search.to

    const gran = search.gran
    if (typeof gran === 'string' && (GRANULARITY_VALUES as readonly string[]).includes(gran)) {
      out.gran = gran as GranularityOverride
    }

    // Accepted as `1` / `'1'` / `'true'` / `true`; normalized to the
    // literal `1` so the type stays a single value, `edit: undefined` is
    // the only "off" state, and the URL the app WRITES matches the one
    // the docs and the e2e selectors use.
    const edit = search.edit
    if (edit === true || edit === 1 || edit === '1' || edit === 'true') out.edit = 1

    // DEV-only. Dropped outright in a production build — Vite folds
    // `import.meta.env.DEV` to `false` and the branch is eliminated, so
    // the gallery has no reachable entry point on a deployed bundle.
    if (import.meta.env.DEV) {
      const fixture = search.fixture
      if (fixture === true || fixture === 1 || fixture === '1' || fixture === 'true') {
        out.fixture = true
      }
    }

    return out
  },
})

function ReportsRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const me = useMe()
  const isGuest = useIsGuest()
  const { canViewReports } = useReportsPermission()

  useEffect(() => {
    if (!getAccessToken()) {
      void navigate({ to: '/login' })
    }
  }, [navigate])

  useEffect(() => {
    // `useIsGuest()` reads NOT-guest while `/api/me` is in flight, so this
    // can never bounce a member off their own page; the `me.isPending`
    // hold below is what keeps the window between mount and resolution
    // from firing a doomed fetch.
    if (isGuest) {
      void navigate({ to: '/' })
    }
  }, [isGuest, navigate])

  if (!getAccessToken()) return null
  if (me.isPending) return <ReportsSkeleton />
  if (isGuest) return null
  if (!canViewReports) return <ReportsNoAccess />

  return <ReportsPage search={search} />
}
